import {BANK_ACCOUNT_BALANCE_GLA_ID, VAT_GLA_ID} from "../../../../../hooks/useGLAccounts.ts";
import {cleanDocumentFileName} from "../../../../../lib/TFICLib.ts";
import type {BookingSource, DrillDownLine} from "../../types/types.ts";
import type {JournalLeg, PanelBooking} from "../types.ts";
import type {BookingIndex} from "./buildBookingIndex.ts";
import {axisIdsOf} from "./groupingAxis.ts";
import type {PresentationSourceType} from "../../../../../generated/enums.ts";
import {VAT_CODE_RATE_LABELS, vatRateLabel} from "../../../../../types/vatCodes.ts";

interface SourceIds {
    documentId: string | null;
    transactionId: string | null;
    manualBookingId: string | null;
    billId: string | null;
    billLineItemId: string | null;
}

const NO_SOURCE_IDS: SourceIds = {
    documentId: null,
    transactionId: null,
    manualBookingId: null,
    billId: null,
    billLineItemId: null,
};

function sourceIds(source: BookingSource | undefined): SourceIds {
    if (!source) return NO_SOURCE_IDS;
    switch (source.source_type_for_presentation) {
        case "BILL":
            return {
                ...NO_SOURCE_IDS,
                documentId: source.document_id ?? null,
                billId: source.bill_id,
                billLineItemId: source.line_item_id ?? null,
            };
        case "TRANSACTION":
            return {...NO_SOURCE_IDS, transactionId: source.transaction_id};
        case "MANUAL_BOOKING":
            return {...NO_SOURCE_IDS, manualBookingId: source.booking_id};
        case "FIXED_ASSET":
        case "MATCH":
            return NO_SOURCE_IDS;
        default: {
            const _exhaustive: never = source;
            return _exhaustive;
        }
    }
}

function displayableText(value: string | null | undefined): string | null {
    if (!value) return null;
    const trimmed = value.trim();
    if (!trimmed) return null;
    if (/^[-–—._]+$/.test(trimmed)) return null;
    if (trimmed.toLowerCase() === "onbekende relatie") return null;
    return trimmed;
}

function billFileLabel(source: BookingSource | undefined): string | null {
    if (source?.source_type_for_presentation !== "BILL" || !source.file_name) return null;
    return displayableText(cleanDocumentFileName(source.file_name));
}

function sourceTitle(line: DrillDownLine): string {
    const source = line.sources[0];
    return (
        displayableText(source?.description)
        ?? displayableText(line.description)
        ?? billFileLabel(source)
        ?? "Geen beschrijving"
    );
}

function sourceRelationName(source: BookingSource | undefined): string | null {
    switch (source?.source_type_for_presentation) {
        case "BILL":
        case "TRANSACTION":
            return displayableText(source.relation_name);
        default:
            return null;
    }
}

function subtitleParts(source: BookingSource | undefined): (string | null)[] {
    if (source?.source_type_for_presentation === "BILL") {
        return [displayableText(source.invoice_number), sourceRelationName(source)];
    }
    return [sourceRelationName(source)];
}

function sourceSubtitle(source: BookingSource | undefined): string | null {
    const parts = subtitleParts(source).filter((part): part is string => part !== null);
    return parts.length ? parts.join(" · ") : null;
}

function sourceVatLabel(source: BookingSource | undefined): string | null {
    switch (source?.source_type_for_presentation) {
        case "BILL":
            return source.vat_rate_percentage == null ? null : vatRateLabel(source.vat_rate_percentage);
        case "TRANSACTION":
            return source.vat_code ? VAT_CODE_RATE_LABELS[source.vat_code] : null;
        default:
            return null;
    }
}

function sourceInvoiceNumber(source: BookingSource | undefined): string | null {
    if (source?.source_type_for_presentation !== "BILL") return null;
    return displayableText(source.invoice_number);
}

function sourceFileName(source: BookingSource | undefined): string | null {
    return billFileLabel(source);
}

function toJournalLegs(
    bookingId: string | null,
    index: BookingIndex,
    accountCode: string,
): JournalLeg[] {
    if (!bookingId) return [];
    const legs = index.get(bookingId) ?? [];
    return legs.map((leg) => ({
        accountCode: leg.accountCode,
        accountLabel: leg.accountLabel,
        description: leg.description,
        debit: leg.debit.signed_amount !== 0 ? leg.debit : null,
        credit: leg.credit.signed_amount !== 0 ? leg.credit : null,
        isCurrentAccount: leg.accountCode === accountCode,
    }));
}

function legsAreBalanced(legs: JournalLeg[]): boolean {
    if (!legs.length) return true;
    const debit = legs.reduce((sum, leg) => sum + (leg.debit?.signed_amount ?? 0), 0);
    const credit = legs.reduce((sum, leg) => sum + (leg.credit?.signed_amount ?? 0), 0);
    return debit === credit;
}

function selectability(
    kind: PresentationSourceType | null,
    accountCode: string,
    ids: SourceIds,
): {selectable: boolean; reason: string | null} {
    if (accountCode === VAT_GLA_ID) {
        return {
            selectable: false,
            reason: "Btw volgt de btw-code van de factuur of transactie en kan hier niet verplaatst worden.",
        };
    }
    if (accountCode === BANK_ACCOUNT_BALANCE_GLA_ID) {
        return {
            selectable: false,
            reason: "Deze regel volgt de banktransactie zelf en kan niet verplaatst worden.",
        };
    }
    if (kind === null) {
        return {selectable: false, reason: "Deze regel heeft geen bron om te verplaatsen."};
    }
    switch (kind) {
        case "MATCH":
            return {
                selectable: false,
                reason: "Dit is een betaling. De kosten- of opbrengstkant komt van de factuur via de match.",
            };
        case "FIXED_ASSET":
            return {
                selectable: false,
                reason: "Deze afschrijving komt uit het afschrijvingsschema van het vaste activum.",
            };
        case "MANUAL_BOOKING":
            return {
                selectable: false,
                reason: "Memoriaalboekingen wijzig je in de memoriaalboeking zelf.",
            };
        case "BILL":
            if (!ids.billLineItemId) {
                return {
                    selectable: false,
                    reason:
                        "Dit is de tegenboeking van de hele factuur, geen factuurregel. " +
                        "Alleen factuurregels op een kosten- of opbrengstrekening kunnen verplaatst worden.",
                };
            }
            return {selectable: true, reason: null};
        case "TRANSACTION":
            return {selectable: true, reason: null};
        default: {
            const _exhaustive: never = kind;
            return _exhaustive;
        }
    }
}

export function toPanelBookings(
    lines: DrillDownLine[],
    index: BookingIndex,
    accountCode: string,
): PanelBooking[] {
    return lines.map((line, position) => {
        const source = line.sources[0];
        const kind = source?.source_type_for_presentation ?? null;
        const legs = toJournalLegs(line.booking_id, index, accountCode);
        const ids = sourceIds(source);
        const {selectable, reason} = selectability(kind, accountCode, ids);
        return {
            key: line.booking_id ? `${line.booking_id}-${position}` : `row-${position}`,
            kind,
            title: sourceTitle(line),
            subtitle: sourceSubtitle(source),
            relationName: sourceRelationName(source),
            invoiceNumber: sourceInvoiceNumber(source),
            fileName: sourceFileName(source),
            date: line.booking_date ?? source?.source_date ?? null,
            ...ids,
            axisIds: axisIdsOf(line.sources),
            debit: line.debit_amount,
            credit: line.credit_amount,
            legs,
            legsAreBalanced: legsAreBalanced(legs),
            selectable,
            notSelectableReason: reason,
            subledger: line.subledger ?? null,
            vatLabel: sourceVatLabel(source),
        };
    });
}
